/**
 * Pure deep link routing logic for the app's global `handleDeepLink` handler
 * (frontend/app/_layout.tsx). Kept separate from the layout component so it
 * can be unit tested without rendering the router/navigation tree.
 *
 * Supports both the custom scheme (`alldrivers://...`) and the https
 * universal link equivalent (`https://<APP_DOMAIN>/...`) for every route,
 * since iOS/Android can hand either form to the app depending on how the
 * link was opened (cold-start vs already-installed tap, Handoff, etc).
 *
 * Handled routes (see issue #245):
 * - checkout-return?self_pay=success  -> /checkout-return (Stripe hosted checkout return;
 *                                        entitlement remains server-authoritative)
 * - auth/magic-link?token=X           -> /(auth)/magic-link (placeholder; real flow in #205)
 * - invitations/accept/<token>        -> /invitations/accept/[token] (existing screen)
 * - partner/verify?token=X[&next=Y]   -> /partner-verify (partner email-possession/access link)
 *
 * Anything else (unknown path, malformed URL) resolves to `{ type: 'unknown' }`
 * so the caller can log a breadcrumb and fall back without crashing or
 * navigating away from wherever the app already is.
 */

export type DeepLinkRoute =
  | { type: 'checkout-return'; selfPay: 'success' | 'canceled' | null }
  | { type: 'auth-magic-link'; token: string | null }
  | { type: 'invitation-accept'; token: string }
  | { type: 'partner-verify'; token: string | null; next: string | null }
  | { type: 'unknown'; url: string; reason: string };

/**
 * Splits a URL's path into non-empty segments regardless of scheme.
 * Handles both `scheme://host/a/b` (host = first "segment" for custom
 * schemes) and `https://domain/a/b` (host = real domain, path = /a/b).
 */
const getPathSegments = (parsed: URL): string[] => {
  const segments: string[] = [];

  // For custom schemes like `alldrivers://checkout-return`, the WHATWG URL
  // parser treats "checkout-return" as the host.
  // For https universal links, host is the real domain and should be
  // excluded — only pathname segments are the route.
  const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:';

  if (!isHttp && parsed.hostname) {
    segments.push(parsed.hostname);
  }

  const pathSegments = parsed.pathname.split('/').filter(Boolean);
  segments.push(...pathSegments);

  return segments;
};

export const parseDeepLink = (url: string): DeepLinkRoute => {
  if (!url || typeof url !== 'string') {
    return { type: 'unknown', url: String(url ?? ''), reason: 'empty-url' };
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { type: 'unknown', url, reason: 'malformed-url' };
  }

  const segments = getPathSegments(parsed);

  if (segments.length === 0) {
    return { type: 'unknown', url, reason: 'no-path' };
  }

  const [first, second, third] = segments;

  if (first === 'checkout-return' || (first === 'checkout' && second === 'return')) {
    const selfPay = parsed.searchParams.get('self_pay');
    return {
      type: 'checkout-return',
      selfPay: selfPay === 'success' || selfPay === 'canceled' ? selfPay : null,
    };
  }

  if (first === 'auth' && second === 'magic-link') {
    return {
      type: 'auth-magic-link',
      token: parsed.searchParams.get('token'),
    };
  }

  if (first === 'invitations' && second === 'accept') {
    const token = third || parsed.searchParams.get('token');
    if (!token) {
      return { type: 'unknown', url, reason: 'invitation-missing-token' };
    }
    return { type: 'invitation-accept', token };
  }

  if (first === 'partner' && second === 'verify') {
    return { type: 'partner-verify', token: parsed.searchParams.get('token'), next: parsed.searchParams.get('next') };
  }

  return { type: 'unknown', url, reason: 'unrecognized-path' };
};

/**
 * Maps a parsed deep link to an expo-router `router.replace()` target.
 * Returns `null` for unknown links — callers should not navigate in that
 * case (stay on the current screen / whatever cold-start resolves to).
 */
export const resolveDeepLinkTarget = (
  route: DeepLinkRoute
): { pathname: string; params?: Record<string, string> } | null => {
  switch (route.type) {
    case 'checkout-return':
      return {
        pathname: '/checkout-return',
        ...(route.selfPay ? { params: { self_pay: route.selfPay } } : {}),
      };
    case 'auth-magic-link':
      return {
        pathname: '/(auth)/magic-link',
        ...(route.token ? { params: { token: route.token } } : {}),
      };
    case 'invitation-accept':
      return {
        pathname: '/invitations/accept/[token]',
        params: { token: route.token },
      };
    case 'partner-verify': {
      // `next` is only a hint; /partner-verify maps it through its own allowlist.
      const params: Record<string, string> = {};
      if (route.token) params.token = route.token;
      if (route.next) params.next = route.next;
      return { pathname: '/partner-verify', ...(Object.keys(params).length ? { params } : {}) };
    }
    case 'unknown':
    default:
      return null;
  }
};
